module Sm.Model.Record

open System
open FsToolkit.ErrorHandling
open MobF

open Sm.Core.Text
open Sm.Core.FractionalOrder

open Sm.Service.Tracking
open Sm.Service.Storage
open Sm.Service.Repository

type Source = {
    Id: Guid
    Text: string
    Metadata: Map<string, string>
}

type State = {
    Id: Guid
    Timestamp: Timestamp
    Text: string
    Metadata: Map<string, string>
    RuntimeInfo: Map<string, obj>
    Changes: int
}

type Msg =
    | SetText of string
    | HandleTextChanged of string
    | SetMetadata of string * string
    | HandleMetadataChanged of string * string
    | ClearMetadata of string
    | HandleMetadataCleared of string
    | Archive
    | Restore
    | ToggleArchive
    | SetRuntimeInfo of string * obj
    | ClearRuntimeInfo of string
    | Scope of Guid
    | Unscope

type IComputed =
    inherit IExtendable<State>
    abstract ArchiveDate: DateTime option
    abstract Tracking: int64 seq
    abstract Scope: Guid option

module Meta =
    type Token =
        | Id of Guid
        | Int of int
        | Bool of bool
        | Order of Order
        | Word of string
        | DateTime of DateTime
        | Date of DateOnly

    type Codec<'T> =
        abstract Parse: string -> 'T option
        abstract Format: 'T -> string option

    type Accessor<'T> =
        abstract Codec: Codec<'T>
        abstract Key: string
        abstract Get: Map<string, string> -> 'T option
        abstract Set: 'T -> Msg
        abstract Clear: Msg
        abstract Update: 'T option -> Msg

    let (|IdValue|_|) = function
        | Match "^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$" [] as s -> Guid.Parse(s) |> Some
        | _ -> None

    let tokenize (value: string) =
        value.Split(' ', StringSplitOptions.RemoveEmptyEntries ||| StringSplitOptions.TrimEntries)
        |> Seq.map (fun token ->
            match token with
            | "true" -> Bool true
            | "false" -> Bool false
            | Match @"^-?\d+$" [] as s -> int s |> Int
            | Match "^~(\w+)$" [Sm.Core.FractionalOrder.Order o] -> Order o
            | IdValue id -> Id id
            | Sm.Core.Text.DateTime date when token.Contains('T') -> DateTime date
            | DateOnly date -> Date date
            | s -> Word s
        )
        |> List.ofSeq

    let format = function
        | [] -> None
        | tokens ->
            tokens
            |> Seq.map (function
                | Id x -> string x
                | Int x -> string x
                | Bool x -> string x
                | Order (OrderString x) -> sprintf "~%s" x
                | Word x -> x
                | DateTime s -> s.ToString("yyyy-MM-ddTHH:mm:ss")
                | Date d -> sprintf "%04i-%02i-%02i" d.Year d.Month d.Day
            )
            |> join " "
            |> Some

    let inline makeAccessor key parse serialize =
        let codec =
            { new Codec<_> with
                member _.Parse(text) = text |> tokenize |> parse
                member _.Format(value) = value |> serialize |> format
            }

        { new Accessor<_> with
            member _.Codec = codec
            member _.Key = key

            member _.Get(metadata) =
                match metadata |> Map.tryFind key with
                | None -> None
                | Some text -> codec.Parse(text)

            member this.Set(value) =
                match codec.Format(value) with
                | None -> this.Clear
                | Some text -> SetMetadata(key, text)

            member _.Clear =
                ClearMetadata key

            member this.Update(value) =
                match value with
                | None -> this.Clear
                | Some x -> this.Set(x)
        }

    let collectOn map messages =
        (map, messages) ||> Seq.fold (fun meta -> function
            | SetMetadata (name, value) -> meta |> Map.add name value
            | ClearMetadata name -> meta |> Map.remove name
            | _ -> meta
        )

    let collect messages =
        collectOn Map.empty messages

module Accessor =
    let archived =
        let read = function
            | [Meta.DateTime d] -> Some d
            | _ -> None
        let write value =
            [Meta.DateTime value]
        Meta.makeAccessor Document.ArchivedField read write

    let scope =
        let read = function
            | [Meta.Id scope] -> Some scope
            | _ -> None
        let write scope =
            [Meta.Id scope]
        Meta.makeAccessor Document.ScopeField read write

type Type = Model<State, Msg, IComputed>

let create (source: Source) dispatch : Type =
    let init () =
        {
            Id = source.Id
            Timestamp =
                Id.parse source.Id
                |> Option.map snd
                |> Option.defaultValue Timestamp.Now
            Text = source.Text
            Metadata = source.Metadata
            RuntimeInfo = Map.empty
            Changes = 0
        }

    let update (state, post, computed: IComputed) = function
        | SetText value ->
            dispatch {
                Adjustment.Id = state.Id
                Field = Document.TextField
                Value = Some value
            }
            post (HandleTextChanged value)
            state

        | HandleTextChanged text ->
            { state with Text = text; Changes = state.Changes + 1 }

        | SetMetadata (name, value) ->
            dispatch {
                Adjustment.Id = state.Id
                Field = name
                Value = Some value
            }
            post (HandleMetadataChanged (name, value))
            state

        | HandleMetadataChanged (name, value) ->
            let metadata = state.Metadata.Add(name, value)
            { state with Metadata = metadata; Changes = state.Changes + 1 }

        | ClearMetadata name ->
            dispatch {
                Adjustment.Id = state.Id
                Field = name
                Value = None
            }
            post (HandleMetadataCleared name)
            state

        | HandleMetadataCleared name ->
            let metadata = state.Metadata.Remove(name)
            { state with Metadata = metadata; Changes = state.Changes + 1 }

        | Archive ->
            post (Accessor.archived.Set DateTime.Now)
            state

        | Restore ->
            post Accessor.archived.Clear
            state

        | ToggleArchive ->
            if computed.ArchiveDate.IsSome then post Restore
            else post Archive
            state

        | SetRuntimeInfo (name, value) ->
            let info = state.RuntimeInfo |> Map.add name value
            { state with RuntimeInfo = info }

        | ClearRuntimeInfo name ->
            let info = state.RuntimeInfo |> Map.remove name
            { state with RuntimeInfo = info }

        | Scope scope ->
            post (Accessor.scope.Set(scope))
            state

        | Unscope ->
            post Accessor.scope.Clear
            state

    let compute state =
        { new IComputed with
            member _.ArchiveDate =
                Accessor.archived.Get state.Metadata

            member _.Tracking =
                match state.Metadata.TryFind Document.TrackerField with
                | None -> Seq.empty
                | Some value -> Data.parse value

            member _.Scope =
                Accessor.scope.Get(state.Metadata)
        }

    Model.useInit init
    |> Model.andUpdateWithComputed update
    |> Model.createWithComputed compute

let fromText code text =
    {
        Id = Id.now code
        Text = text
        Metadata = Map.empty
    }

let fromTextAndMetadata code text metadata =
    {
        Id = Id.now code
        Text = text
        Metadata = metadata
    }

let fromStorageFields id fields =
    option {
        let! text =
            fields
            |> Map.tryFind Document.TextField
            |> Option.bind (fun (x: Document.Register) -> x.Value)

        let metadata =
            fields
            |> Seq.choose (function
                | KeyValue(Document.TextField, _) -> None
                | KeyValue(k, { Value = Some v }) -> Some (k, v)
                | _ -> None
            )
            |> Map

        return  { Id = id; Text = text; Metadata = metadata }
    }

let fromStorageContent (content: Document.Content) =
    fromStorageFields content.Id content.Fields

let track data (record: Type) =
    let value = 
        data
        |> Seq.append record.Computed.Tracking
        |> Seq.distinct
        |> Data.serialize

    let msg = SetMetadata (Document.TrackerField, value)
    record.Post(msg)
